import { forward, type VehicleEntity } from './entities';

/**
 * Vehicle-vs-vehicle: each vehicle is two circles (front/back) along its axis. Overlapping
 * circles push both vehicles apart (by inverse mass) and bleed speed. Cheap and stable.
 *
 * (owner, Sep 16: "driving into a stopped train does too much damage and pushes the car away — it should be equal to
 * driving into a building") A train standing on its rails is a wall: it gives up none of the overlap, keeps its speed, and
 * the car bounces off it the way physics.ts bounces one off a wall. The damage is game.ts's to decide.
 */
export const WALL_BOUNCE = -0.25;
/** a vehicle nothing on the road can shift: a train at a stand (a moving one throws what it hits, as it should) */
export function immovable(v: VehicleEntity): boolean { return v.def.class === 'train' && Math.abs(v.speed) < 0.5; }
export function collideVehicles(vs: VehicleEntity[], onHit: (a: VehicleEntity, b: VehicleEntity, impulse: number) => void, canStand: (v: VehicleEntity, x: number, y: number) => boolean = () => true, apart: (a: VehicleEntity, b: VehicleEntity) => boolean = () => false): void {
  const n = vs.length;
  const circ = (v: VehicleEntity): { x: number; y: number; r: number }[] => {
    const [w, l] = v.def.size;
    const r = w / 2;
    const [fx, fy] = forward(v.angle);
    const off = Math.max(0, l / 2 - r);
    // a nose and a tail circle, and as many between as the length needs: a wagon seventeen tiles long had nothing but its
    // two ends, and a car drove through the middle of the train (owner, Sep 11)
    const k = Math.max(1, Math.ceil(off / r));
    const out: { x: number; y: number; r: number }[] = [];
    for (let i = 0; i <= k; i++) { const d = -off + (2 * off * i) / k; out.push({ x: v.x + fx * d, y: v.y + fy * d, r }); }
    return out;
  };
  // (perf, Sep 17 — docs/PERF.md) a sweep along x: the vehicles in x order, and a pair is looked at only while the second is within the
  // longest possible reach of the first; and the cheap tests come before `apart`, which reaches for the deck and the water — it was
  // asked for every one of a city's 17 000 pairs a step, a quarter of the collider's time
  const order = vs.map((_, i) => i).sort((i, j) => vs[i].x - vs[j].x);
  let longest = 0; for (const v of vs) if (v.def.size[1] > longest) longest = v.def.size[1];
  for (let oi = 0; oi < n; oi++) {
    const a = vs[order[oi]];
    if ((a.alt > 0.5 && a.def.flight) || a.drone) continue; // (a drone taking off or landing is under everybody's wheels, not in their way)
    let ca: { x: number; y: number; r: number }[] | undefined;
    const reachA = (a.def.size[1] + longest) / 2;
    for (let oj = oi + 1; oj < n; oj++) {
      const b = vs[order[oj]];
      if (b.x - a.x > reachA) break; // (sorted: nothing further along can touch a)
      const maxR = (a.def.size[1] + b.def.size[1]) / 2;
      if (Math.abs(a.x - b.x) > maxR || Math.abs(a.y - b.y) > maxR) continue;
      if ((b.alt > 0.5 && b.def.flight) || b.drone || Math.abs(a.alt - b.alt) > 1.5 || apart(a, b)) continue; // aircraft, different overpass levels, a boat under the deck
      ca ??= circ(a);
      const cb = circ(b);
      // every overlapping circle pair pushes the cars apart (so long cars do not sink into each other
      // sideways); the deepest contact alone carries the speed impulse
      // a standing train is a wall: it gives up none of the overlap and none of its speed (owner, Sep 16)
      const wallA = immovable(a) && !immovable(b), wallB = immovable(b) && !immovable(a);
      const ma = a.def.stats.mass, mb = b.def.stats.mass, tot = ma + mb;
      const shareA = wallA ? 0 : wallB ? 1 : mb / tot, shareB = wallB ? 0 : wallA ? 1 : ma / tot; // how much of the overlap each gives up
      let best: { nx: number; ny: number; pen: number } | undefined;
      // the overlap is resolved ONCE per pair, from the deepest contact. Pushing on every overlapping circle pair piled the
      // corrections up — against a long wagon (a dozen circles) a car was thrown clear of the rails (owner, Sep 16)
      const pushes: { nx: number; ny: number; pen: number }[] = [];
      for (const p of ca) for (const q of cb) {
        const dx = q.x - p.x, dy = q.y - p.y;
        const d = Math.hypot(dx, dy), min = p.r + q.r;
        if (d >= min || d === 0) continue;
        const pen = min - d, nx = dx / d, ny = dy / d;
        pushes.push({ nx, ny, pen });
        if (!best || pen > best.pen) best = { nx, ny, pen };
      }
      if (best) {
        // the direction is the blend of every contact (so a long car does not sink in sideways), the distance is the
        // deepest one alone
        let sx = 0, sy = 0; for (const p of pushes) { sx += p.nx * p.pen; sy += p.ny * p.pen; }
        const m = Math.hypot(sx, sy); const nx = m > 1e-6 ? sx / m : best.nx, ny = m > 1e-6 ? sy / m : best.ny, pen = best.pen;
        // never shove a car off a bridge or into a lake: an illegal push is taken by the other car instead
        const ax = a.x - nx * pen * shareA, ay = a.y - ny * pen * shareA, bx = b.x + nx * pen * shareB, by = b.y + ny * pen * shareB;
        const okA = canStand(a, ax, ay), okB = canStand(b, bx, by);
        if (okA && okB) { a.x = ax; a.y = ay; b.x = bx; b.y = by; }
        else if (okA) { a.x -= nx * pen; a.y -= ny * pen; }
        else if (okB) { b.x += nx * pen; b.y += ny * pen; }
      }
      if (!best) continue;
      const { nx, ny } = best;
      // relative speed along the normal
      const [afx, afy] = forward(a.angle), [bfx, bfy] = forward(b.angle);
      const an = afx * nx + afy * ny, bn = bfx * nx + bfy * ny; // how much of each car's axis lies along the contact normal
      const va = a.speed * an, vb = b.speed * bn;
      const rel = va - vb;
      if (rel > 0.5) {
        const impulse = rel;
        // a car hit from the side cannot be sped up along its own axis (that used to snowball into
        // hundreds of m/s in a pile-up); speeds also never leave the vehicle's own range
        if (wallB) a.speed *= WALL_BOUNCE; // into a standing train: the same small bounce a wall gives (physics.ts)
        else if (wallA) b.speed *= WALL_BOUNCE;
        else { a.speed -= impulse * (mb / tot) * 0.9 * an; b.speed += impulse * (ma / tot) * 0.9 * bn; } // signed: a reversing into b is slowed, not sped up
        const capA = a.def.stats.maxSpeed * 1.2, capB = b.def.stats.maxSpeed * 1.2;
        a.speed = Math.max(-capA, Math.min(capA, a.speed)); b.speed = Math.max(-capB, Math.min(capB, b.speed));
        onHit(a, b, impulse);
      }
    }
  }
}
